import { App } from 'antd'

import { restClient } from '@/Core'
import { notify } from '@/Ship/Helpers/notify'
import { parseApiErrorDescription } from '@/Ship/Helpers/parseApiError'
import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'

import { StatusChanger, type StatusOption } from './StatusBadge'

interface StatusCellProps {
    /** Поточне значення статусу */
    value: string
    /** Визначення поля з ресурсу: options, getChangeUrl, confirmOnValues, confirmText, onAfterChange, inputProps */
    field: any
    /** Запис (з '@id' для PATCH) */
    item: any
    /** Контекст CRUD (таблиця або картка); якщо не передано, береться з React-контексту */
    crudContext?: any
    /**
     * Виклик після успішної зміни замість закриття картки: картка лишається відкритою,
     * а викликач сам оновлює показане значення
     */
    onChanged?: (_option: StatusOption) => void
}

/**
 * Комірка статусу для таблиці й картки запису: бейдж із меню зміни статусу.
 * Повторює логіку Core StatusDropdown: перевірка права update_status, підтвердження для
 * confirmOnValues, PATCH на getChangeUrl або '@id' запису, повідомлення, перезавантаження
 * списку, закриття картки, onAfterChange поля.
 */
export const StatusCell = ({ value, field, item, crudContext: crudContextProp, onChanged }: StatusCellProps) => {
    const { modal } = App.useApp()
    const contextFromProvider = useCrudResourceContext()
    const crudContext = crudContextProp ?? contextFromProvider

    const options: StatusOption[] = Array.isArray(field?.options) ? field.options : []
    const dangerValues: string[] = Array.isArray(field?.confirmOnValues) ? field.confirmOnValues : []

    const editable =
        crudContext?.accessFn?.({ accessMode: 'update_status', accessItem: item, crudContext }) === true &&
        field?.inputProps?.disabled !== true

    const change = async (option: StatusOption) => {
        if (dangerValues.includes(option.value) && field?.confirmText) {
            const confirmed = await new Promise<boolean>((resolve) => {
                modal.confirm({
                    title: 'Підтвердження',
                    content: field.confirmText,
                    okText: 'Так',
                    okButtonProps: { danger: true },
                    cancelText: 'Скасувати',
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                })
            })
            if (!confirmed) return
        }

        const itemId = item?.['@id']
        const currentOption = options.find((o) => o.value === value)
        const changeUrl =
            typeof field?.getChangeUrl === 'function'
                ? field.getChangeUrl({ currentOption, options, item, field })
                : itemId

        if (!changeUrl || typeof changeUrl !== 'string') {
            notify.error('Не вдалося визначити ідентифікатор запису')
            return
        }

        try {
            await restClient.patch(changeUrl, { json: { status: option.value } })
        } catch (error: any) {
            // Опис — лише повідомлення сервера (валідація, причина), без технічного тексту запиту
            const description = await parseApiErrorDescription(error, '')
            notify.error('Не вдалося змінити статус', description ? { description } : undefined)
            return
        }

        notify.success(`Статус змінено на «${option.label}»`)

        // Таблиця: перезавантажуємо список; картка: список батька і закриваємо картку
        // (або віддаємо керування викликачу через onChanged)
        const listContext = crudContext?.listCrudContext?.listContext ?? crudContext?.listContext
        listContext?.actions?.load?.(true)
        if (onChanged) onChanged(option)
        else crudContext?.closeResourceDetail?.()

        if (typeof field?.onAfterChange === 'function') field.onAfterChange({ option, item, crudContext })
    }

    return (
        <StatusChanger
            value={value}
            options={options}
            onChange={change}
            dangerValues={dangerValues}
            editable={editable}
        />
    )
}
